import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface TimelineSection<TItem> {
  dayKey: string | null;
  dayLabel: string;
  items: readonly TItem[];
}

export interface TimelineViewerProps<TItem> {
  sections: readonly TimelineSection<TItem>[];
  renderItem: (item: TItem) => ReactNode;
  getItemKey: (item: TItem) => string;
  renderMarker?: (item: TItem) => ReactNode;
  header?: ReactNode;
  empty?: ReactNode;
  ariaLabel?: string;
  className?: string;
  sectionClassName?: string;
  itemClassName?: string;
}

function DayMarker({ label, isLatest }: { label: string; isLatest?: boolean }) {
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs font-medium text-muted-foreground">
      {isLatest && <span className="h-2 w-2 rounded-full bg-green-600" aria-label="Latest" />}
      {label}
    </div>
  );
}

function DefaultMarker() {
  return (
    <span
      className="block h-2 w-2 rounded-full bg-foreground/60 ring-4 ring-background"
      aria-hidden="true"
    />
  );
}

/** Presentational timeline with day dividers. Caller owns data modeling. */
export function TimelineViewer<TItem>({
  sections,
  renderItem,
  getItemKey,
  renderMarker,
  header,
  empty,
  ariaLabel = 'Timeline',
  className,
  sectionClassName,
  itemClassName,
}: TimelineViewerProps<TItem>) {
  const isEmpty = sections.length === 0 || sections.every((s) => s.items.length === 0);

  if (isEmpty) {
    return (
      <div className={className}>
        {header}
        {empty}
      </div>
    );
  }

  return (
    <div className={className}>
      {header}

      <div>
        {sections.map((section, index) => (
          <section key={section.dayKey ?? 'unknown'} className={sectionClassName}>
            <DayMarker label={section.dayLabel} isLatest={index === 0} />

            <ol className="ml-3" role="list" aria-label={ariaLabel}>
              {section.items.map((item) => {
                const marker = renderMarker ? renderMarker(item) : <DefaultMarker />;

                return (
                  <li
                    key={getItemKey(item)}
                    className={cn('relative border-l border-border py-3 pl-6', itemClassName)}
                  >
                    {marker != null && (
                      <div className="absolute left-0 top-4 -translate-x-1/2">{marker}</div>
                    )}
                    <div className="min-w-0">{renderItem(item)}</div>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
      </div>
    </div>
  );
}
